import React, { useState, useRef } from "react";
import { View } from "react-native";
import { useRouter } from "expo-router";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { investigationService } from "@/services/investigationService";
import useAuth from "@/hooks/useAuth";
import Head from "expo-router/head";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { useTheme } from "@/hooks/useTheme";
import { getSaveBehavior } from "@/lib/saveSettings";
import { supabase } from "@/lib/supabase-client";
import {
  InvestigationCreateForm,
  InvestigationCreateFormRef,
  InvestigationPayload,
  INVESTIGATION_STATUS_OPTIONS,
  getInvestigationStatusColor,
} from "@/components/screens/investigation/NewInvestigationForm";

export {
  INVESTIGATION_STATUS_OPTIONS,
  getInvestigationStatusColor,
} from "@/components/screens/investigation/NewInvestigationForm";

export default function NewInvestigationPage() {
  const toast = useToast();
  const router = useRouter();
  const { colors } = useTheme();
  const { tenantId } = useAuth();
  const { triggerRefresh } = useRowSelectionContext();

  const [name, setName] = useState("");
  const [status, setStatus] = useState("PENDING");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const formRef = useRef<InvestigationCreateFormRef>(null);

  const handleCreate = async (payload: InvestigationPayload) => {
    if (!tenantId) {
      toast.showToast({
        title: "Error",
        message: "Tenant ID not found",
        toastType: "error",
      });
      return;
    }

    // Validation
    if (!payload.investigation.name.trim()) {
      toast.showToast({
        title: "Validation Error",
        message: "Investigation name is required",
        toastType: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const { investigation } = payload;

      // Build meta_data with new fields
      const meta_data: Record<string, any> = {};
      if (investigation.lead_investigator_id) {
        meta_data.lead_investigator_id = investigation.lead_investigator_id;
      }
      if (investigation.incident_type_id) {
        meta_data.incident_type_id = investigation.incident_type_id;
      }
      if (investigation.findings?.trim()) {
        meta_data.findings = investigation.findings.trim();
      }
      if (investigation.recommendations?.trim()) {
        meta_data.recommendations = investigation.recommendations.trim();
      }

      // Build incidents jsonb array
      const incidentsJson = investigation.incidents.map((i) => ({
        id: i.id,
        name: i.name,
        status: i.status || null,
      }));

      const newInvestigation = await investigationService.addInvestigation({
        tenant_id: tenantId,
        name: investigation.name.trim(),
        description: investigation.description?.trim() || null,
        details: investigation.details?.trim() || null,
        status: investigation.status,
        meta_data,
        incidents: incidentsJson,
      });

      // Insert linked incidents into link table
      if (investigation.incidents.length > 0 && newInvestigation?.id) {
        const linkRows = investigation.incidents.map((incident) => ({
          id: newInvestigation.id,
          tenant_id: tenantId,
          incident_id: incident.id,
        }));

        const { error: linkError } = await supabase.from("investigations_incidents" as any).insert(linkRows);

        if (linkError) {
          console.error("Failed to insert investigation-incident links:", linkError);
        }
      }

      toast.showToast({
        title: "Investigation Created",
        message: "Investigation has been created successfully",
        toastType: "success",
      });

      triggerRefresh("investigation");

      // Handle save behavior setting
      const behavior = await getSaveBehavior("investigation");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: newInvestigation?.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to Create Investigation",
        message: err.message || "An error occurred while creating the investigation",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setName("");
    setStatus("PENDING");
    formRef.current?.handleReset();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Investigation</title>
        <meta name="description" content="Create a new investigation" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="investigation"
          name={name}
          onNameChange={(n) => {
            setName(n);
            formRef.current?.updateInvestigationName(n);
          }}
          namePlaceholder="Investigation name"
          dirty={dirty}
          saving={saving}
          onSave={() => formRef.current?.handleSave()}
          onCancel={handleReset}
          status={status}
          onStatusChange={(s) => {
            setStatus(s);
            formRef.current?.updateInvestigationStatus(s);
          }}
          statusOptions={INVESTIGATION_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getInvestigationStatusColor(opt.text, colors),
          }))}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <InvestigationCreateForm
            ref={formRef}
            initialInvestigation={{ name, status }}
            onChange={() => {}}
            onSave={handleCreate}
            onDirtyChange={setDirty}
          />
        </View>
      </View>
    </>
  );
}
